import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { motion } from "framer-motion";
import AdminService from "../../Services/AdminService";
import SystemDataService from "../../Services/SystemDataService";
import Layout from "../../Utils/Navbar/navbar-layout-component";
import Footer from "../../Utils/Footer/footer-component";
import MediaComponent from "../../Utils/Media/media-component";
import { getAdminOrderDetailStrings } from "../../Public/admin-order-detail-data.js";
import { ButtonFlowHover, ButtonChevronReveal } from "../../Utils/Buttons/button-component";
import { setToast } from "../../Redux/Toast/ToastActions";

const DELIVERY_STATUS_FLOW = ['received', 'processing', 'ready_for_shipping', 'shipped', 'delivered', 'done', 'payment_failed'];
const PICKUP_STATUS_FLOW   = ['received', 'processing', 'ready_for_pickup',   'picked_up', 'done', 'payment_failed'];

const STATUS_CONFIG = {
    paid:               { color: 'text-secondary-500', dot: 'bg-secondary-500', badge: 'bg-white text-secondary-500 border-secondary-300' },
    received:           { color: 'text-teal-600',      dot: 'bg-teal-400',     badge: 'bg-teal-50 text-teal-600 border-teal-200' },
    processing:         { color: 'text-yellow-600',    dot: 'bg-yellow-400',   badge: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    ready_for_shipping: { color: 'text-indigo-600',    dot: 'bg-indigo-400',   badge: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
    ready_for_pickup:   { color: 'text-purple-600',    dot: 'bg-purple-400',   badge: 'bg-purple-50 text-purple-600 border-purple-200' },
    shipped:            { color: 'text-blue-600',      dot: 'bg-blue-400',     badge: 'bg-blue-50 text-blue-600 border-blue-200' },
    picked_up:          { color: 'text-emerald-600',   dot: 'bg-emerald-400',  badge: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    delivered:          { color: 'text-green-600',     dot: 'bg-green-400',    badge: 'bg-green-50 text-green-600 border-green-200' },
    done:               { color: 'text-green-700',     dot: 'bg-green-500',    badge: 'bg-green-100 text-green-700 border-green-300' },
    payment_failed:     { color: 'text-red-600',       dot: 'bg-red-400',      badge: 'bg-red-50 text-red-600 border-red-200' },
};

const fade = { hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } };

function AdminOrderDetail() {
    const { orderId } = useParams();
    const navigate    = useNavigate();
    const dispatch    = useDispatch();
    const { language, dir, links = [] } = useSelector((s) => s.systemData || {});

    const t            = getAdminOrderDetailStrings(language);
    const rolloLogoUrl = SystemDataService.byLabel(links, 'Rollo Logo')?.image || '';

    const statusIcons = useMemo(() => ({
        paid:               SystemDataService.byLabel(links, 'Status Paid Icon'),
        received:           SystemDataService.byLabel(links, 'Status Processing Icon'),
        processing:         SystemDataService.byLabel(links, 'Status Processing Icon'),
        ready_for_shipping: SystemDataService.byLabel(links, 'Status Shipped Icon'),
        ready_for_pickup:   SystemDataService.byLabel(links, 'Status Processing Icon'),
        shipped:            SystemDataService.byLabel(links, 'Status Shipped Icon'),
        picked_up:          SystemDataService.byLabel(links, 'Status Delivered Icon'),
        delivered:          SystemDataService.byLabel(links, 'Status Delivered Icon'),
        done:               SystemDataService.byLabel(links, 'Status Delivered Icon'),
        payment_failed:     SystemDataService.byLabel(links, 'Status Failed Icon'),
    }), [links]);

    const [order, setOrder]               = useState(null);
    const [zohoCustomer, setZohoCustomer] = useState(null);
    const [loading, setLoading]           = useState(true);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [saving, setSaving]             = useState(false);
    const [printing, setPrinting]             = useState(false);
    const [buyingLabel, setBuyingLabel]       = useState(false);
    const [downloadingInvoice, setDownloadingInvoice] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        AdminService.getOrders()
            .then(orders => {
                const found = orders.find(o => o.id === orderId);
                setOrder(found || null);
                if (found) setSelectedStatus(found.status);
                if (found?.email) {
                    AdminService.getZohoCustomer(found.email).then(c => setZohoCustomer(c || null));
                }
            })
            .finally(() => setLoading(false));
    }, [orderId]);

    const isDirty = order && selectedStatus !== order.status;

    const handlePrint = async () => {
        if (!order?.labelUrl || printing) return;
        setPrinting(true);
        const result = await AdminService.printLabel({ labelUrl: order.labelUrl, orderId: order.id });
        dispatch(setToast(result ? 'success' : 'error', result ? t.print.successToast : t.print.errorToast));
        setPrinting(false);
    };

    const handleDownloadInvoice = async () => {
        if (downloadingInvoice) return;
        setDownloadingInvoice(true);
        const ok = await AdminService.downloadInvoice(order.id);
        if (!ok) dispatch(setToast('error', 'No Zoho invoice found for this order'));
        setDownloadingInvoice(false);
    };

    const handleBuyLabel = async () => {
        if (!order?.easyshipShipmentId || buyingLabel) return;
        setBuyingLabel(true);
        const result = await AdminService.buyOrderLabel(order.id);
        if (result?.labelUrl) {
            setOrder(prev => ({ ...prev, labelUrl: result.labelUrl }));
            dispatch(setToast('success', t.print.buySuccessToast));
        } else {
            dispatch(setToast('error', t.print.buyErrorToast));
        }
        setBuyingLabel(false);
    };

    const handleUpdate = async () => {
        if (!isDirty || saving) return;
        setSaving(true);
        const updated = await AdminService.updateOrderStatus(order.id, selectedStatus);
        if (updated) {
            setOrder(updated);
            dispatch(setToast('success', `${t.status.successToast} → ${t.status.labels[selectedStatus] || selectedStatus}`));
        } else {
            dispatch(setToast('error', t.status.errorToast));
        }
        setSaving(false);
    };

    const isFailedOrder  = order?.status === 'payment_failed';
    const statusFlow     = order?.includeDelivery ? DELIVERY_STATUS_FLOW : PICKUP_STATUS_FLOW;
    const courierInfo    = useMemo(() => SystemDataService.courierInfo(links, order), [order, links]);
    const config         = STATUS_CONFIG[order?.status]        || STATUS_CONFIG.paid;
    const selectedConfig = STATUS_CONFIG[selectedStatus]       || STATUS_CONFIG.paid;
    const date = order?.createdAt
        ? new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
        : '—';

    return (
        <div className="min-h-screen bg-gray-50 font-primary">
            <Layout>
                <div dir={dir} className={`max-w-5xl mx-auto px-6 py-12 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>

                    {/* ── Back ── */}
                    <button
                        onClick={() => navigate('/admin')}
                        className="flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-primary-950 transition-colors mb-10 group"
                    >
                        <svg className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                        {t.nav.back}
                    </button>

                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="w-6 h-6 border-2 border-primary-950 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : !order ? (
                        <div className="flex items-center justify-center h-64">
                            <p className="text-sm font-medium text-gray-300">{t.hero.notFound}</p>
                        </div>
                    ) : (
                        <motion.div
                            variants={{ show: { transition: { staggerChildren: 0.08 } } }}
                            initial="hidden"
                            animate="show"
                            className="flex flex-col gap-6"
                        >
                            {/* ── Hero card ── */}
                            <motion.div variants={fade} className="bg-primary-950 rounded-[2rem] p-8 lg:p-10">
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                                    <div>
                                        <p className="text-xs font-bold text-white/30 uppercase tracking-widest mb-3">
                                            {t.hero.eyebrow}
                                        </p>
                                        <h1
                                            className="text-4xl lg:text-5xl text-white uppercase leading-none  italic mb-6"
                                            style={{ fontFamily: 'Impact, sans-serif' }}
                                        >
                                            {order.id}
                                        </h1>
                                        <div className="flex flex-wrap gap-x-8 gap-y-3">
                                            {(zohoCustomer?.contact_name || order.shippingName) && (
                                                <div>
                                                    <p className="text-xs font-bold text-white/30 mb-0.5">{t.customer.name}</p>
                                                    <p className="text-sm font-bold text-white">{zohoCustomer?.contact_name || order.shippingName}</p>
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-xs font-bold text-white/30 mb-0.5">{t.meta.customer}</p>
                                                <a href={`mailto:${order.email}`} className="text-sm font-bold text-white/80 hover:text-white transition-colors">{order.email}</a>
                                            </div>
                                            {(zohoCustomer?.phone || order.phone) && (
                                                <div>
                                                    <p className="text-xs font-bold text-white/30 mb-0.5">{t.customer.phone}</p>
                                                    <a href={`tel:${zohoCustomer?.phone || order.phone}`} className="text-sm font-bold text-white/80 hover:text-white transition-colors">
                                                        {zohoCustomer?.phone || order.phone}
                                                    </a>
                                                </div>
                                            )}
                                            <div>
                                                <p className="text-xs font-bold text-white/30 mb-0.5">{t.meta.date}</p>
                                                <p className="text-sm font-bold text-white">{date}</p>
                                            </div>
                                            {order.easyshipShipmentId && (
                                                <div>
                                                    <p className="text-xs font-bold text-white/30 mb-0.5">{t.hero.shipmentId}</p>
                                                    <p className="text-sm font-bold text-white font-mono">{order.easyshipShipmentId}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Total + status */}
                                    <div className="flex flex-col items-start lg:items-end gap-3 shrink-0">
                                        <p className="text-4xl font-black text-secondary-500 italic er">
                                            ${order.total?.toFixed(2)}
                                        </p>
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${config.badge}`}>
                                            <span className={`w-2 h-2 rounded-full shrink-0 ${config.dot}`} />
                                            {t.status.labels[order.status] || order.status}
                                        </span>
                                        {order.trackingUrl && (
                                            <a
                                                href={order.trackingUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1.5 text-xs font-bold text-white/40 hover:text-white transition-colors"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                                </svg>
                                                {t.shipping.trackShipment}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </motion.div>

                            {/* ── Body grid ── */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                                {/* ── Left: Items + Shipping ── */}
                                <motion.div variants={fade} className="lg:col-span-2 flex flex-col gap-6">

                                    {/* Items card */}
                                    <div className="bg-white rounded-[2rem] overflow-hidden border border-gray-100">
                                        <div className="px-8 pt-7 pb-5 border-b border-gray-50 flex items-center justify-between">
                                            <div>
                                                <p className="text-xs font-bold text-primary-950/30 mb-0.5">{t.items.sectionLabel}</p>
                                                <h2 className="text-2xl text-primary-950 uppercase leading-none" style={{ fontFamily: 'Impact, sans-serif' }}>
                                                    {(order.items || []).length} {t.items.sectionLabel}
                                                </h2>
                                            </div>
                                            <span className="text-2xl font-black text-primary-950 italic er">
                                                ${order.total?.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="p-5 space-y-2.5">
                                            {(order.items || []).length === 0 ? (
                                                <p className="text-sm font-medium text-gray-300 text-center py-8">{t.items.noItems}</p>
                                            ) : (order.items || []).map((item, i) => (
                                                <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-gray-50 hover:bg-gray-100/60 transition-colors">
                                                    {item.image && (
                                                        <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-white border border-gray-100 p-1.5">
                                                            <img src={item.image} alt={item.name} className="w-full h-full object-contain mix-blend-multiply" />
                                                        </div>
                                                    )}
                                                    <div className="flex-grow min-w-0">
                                                        <p className="font-bold text-sm text-primary-950 truncate">{item.name}</p>
                                                        <p className="text-xs font-medium text-gray-400 mt-0.5">{t.items.qty} {item.quantity}</p>
                                                    </div>
                                                    <span className="font-black text-sm text-primary-950 italic shrink-0">
                                                        ${(item.price * item.quantity).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Customer info card */}
                                    <div className="bg-white rounded-[2rem] border border-gray-100 p-7">
                                        <div className="flex items-center justify-between mb-4">
                                            <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest">
                                                {t.customer.sectionLabel}
                                            </p>
                                            {zohoCustomer && (
                                                <span className="text-[9px] font-black uppercase tracking-widest text-white bg-primary-950 px-2 py-0.5 rounded-full">
                                                    Zoho
                                                </span>
                                            )}
                                        </div>
                                        <div className="divide-y divide-gray-50">
                                            {(zohoCustomer?.contact_name || order.shippingName) && (
                                                <div className="flex items-center justify-between py-3">
                                                    <span className="text-xs font-bold text-gray-400">{t.customer.name}</span>
                                                    <span className="text-xs font-bold text-primary-950">{zohoCustomer?.contact_name || order.shippingName}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between py-3">
                                                <span className="text-xs font-bold text-gray-400">{t.customer.email}</span>
                                                <a href={`mailto:${order.email}`} className="text-xs font-bold text-secondary-500 hover:underline">{order.email}</a>
                                            </div>
                                            {(zohoCustomer?.phone || order.phone) && (
                                                <div className="flex items-center justify-between py-3">
                                                    <span className="text-xs font-bold text-gray-400">{t.customer.phone}</span>
                                                    <a href={`tel:${zohoCustomer?.phone || order.phone}`} className="text-xs font-bold text-primary-950 hover:text-secondary-500 transition-colors">
                                                        {zohoCustomer?.phone || order.phone}
                                                    </a>
                                                </div>
                                            )}
                                            {zohoCustomer?.billing_address?.street && (
                                                <div className="flex items-start justify-between py-3 gap-4">
                                                    <span className="text-xs font-bold text-gray-400 shrink-0">{t.customer.address}</span>
                                                    <div className="text-right">
                                                        <p className="text-xs font-bold text-primary-950">{zohoCustomer.billing_address.street}</p>
                                                        {(zohoCustomer.billing_address.city || zohoCustomer.billing_address.state) && (
                                                            <p className="text-xs font-medium text-gray-500 mt-0.5">
                                                                {[zohoCustomer.billing_address.city, zohoCustomer.billing_address.state, zohoCustomer.billing_address.zip].filter(Boolean).join(', ')}
                                                            </p>
                                                        )}
                                                        {zohoCustomer.billing_address.country && (
                                                            <p className="text-xs font-medium text-gray-400">{zohoCustomer.billing_address.country}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Shipping info card */}
                                    <div className="bg-white rounded-[2rem] border border-gray-100 p-7">
                                        <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest mb-4">
                                            {t.shipping.sectionLabel}
                                        </p>
                                        <div className="divide-y divide-gray-50">

                                            {/* ── Fulfillment method ── */}
                                            <div className="flex items-center justify-between py-3">
                                                <span className="text-xs font-bold text-gray-400">{t.shipping.method}</span>
                                                {order.includeDelivery ? (
                                                    <div className="flex items-center gap-2">
                                                        {courierInfo?.logoUrl && (
                                                            <img src={courierInfo.logoUrl} alt={courierInfo.name} className="h-4 w-auto object-contain" />
                                                        )}
                                                        <span className="text-xs font-bold text-primary-950">
                                                            {courierInfo?.name || 'Delivery'}
                                                        </span>
                                                        {courierInfo?.transitDays && (
                                                            <span className="text-xs font-medium text-gray-400">
                                                                · {courierInfo.transitDays}d
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1.5">
                                                        <svg className="w-3.5 h-3.5 text-primary-950" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                                        </svg>
                                                        <span className="text-xs font-bold text-primary-950">{t.shipping.pickup}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* ── Ship-to address ── */}
                                            {order.includeDelivery && (order.shippingName || order.shippingAddress?.line1) && (
                                                <div className="flex items-start justify-between py-3 gap-4">
                                                    <span className="text-xs font-bold text-gray-400 shrink-0">{t.shipping.customer}</span>
                                                    <div className="text-right">
                                                        {order.shippingName && (
                                                            <p className="text-xs font-bold text-primary-950">{order.shippingName}</p>
                                                        )}
                                                        {order.shippingAddress?.line1 && (
                                                            <p className="text-xs font-medium text-gray-500 mt-0.5">{order.shippingAddress.line1}</p>
                                                        )}
                                                        {(order.shippingAddress?.city || order.shippingAddress?.state || order.shippingAddress?.postal_code) && (
                                                            <p className="text-xs font-medium text-gray-500">
                                                                {[order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.postal_code].filter(Boolean).join(', ')}
                                                            </p>
                                                        )}
                                                        {order.shippingAddress?.country && (
                                                            <p className="text-xs font-medium text-gray-400">{order.shippingAddress.country}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {order.easyshipShipmentId && (
                                                <div className="flex items-center justify-between py-3">
                                                    <span className="text-xs font-bold text-gray-400">{t.shipping.easyshipId}</span>
                                                    <span className="text-xs font-bold text-primary-950 font-mono">{order.easyshipShipmentId}</span>
                                                </div>
                                            )}
                                            {order.labelUrl && (
                                                <div className="flex items-center justify-between py-3">
                                                    <span className="text-xs font-bold text-gray-400">{t.shipping.label}</span>
                                                    <a
                                                        href={order.labelUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-xs font-bold text-secondary-500 hover:text-secondary-600 transition-colors underline underline-offset-2"
                                                    >
                                                        {t.shipping.labelDownload}
                                                    </a>
                                                </div>
                                            )}
                                            {order.trackingUrl && (
                                                <div className="flex items-center justify-between py-3">
                                                    <span className="text-xs font-bold text-gray-400">{t.shipping.easyshipTrack}</span>
                                                    <a
                                                        href={order.trackingUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1.5 text-xs font-bold text-primary-950 hover:text-secondary-500 transition-colors"
                                                    >
                                                        {t.shipping.open}
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                                        </svg>
                                                    </a>
                                                </div>
                                            )}
                                            <div className="flex items-center justify-between py-3">
                                                <span className="text-xs font-bold text-gray-400">{t.shipping.liveTracker}</span>
                                                <a
                                                    href={`/track?id=${order.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex items-center gap-1.5 text-xs font-bold text-secondary-500 hover:text-secondary-600 transition-colors"
                                                >
                                                    <span className="w-1.5 h-1.5 rounded-full bg-secondary-500 animate-pulse shrink-0" />
                                                    {t.shipping.open}
                                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                                    </svg>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* ── Right: Status + Print ── */}
                                <motion.div variants={fade} className="flex flex-col gap-4">

                                    {/* Status selector */}
                                    <div className={`bg-white rounded-[2rem] border overflow-hidden ${isFailedOrder ? 'border-red-100' : 'border-gray-100'}`}>
                                        <div className="px-6 pt-7 pb-5 border-b border-gray-50">
                                            <p className="text-xs font-bold text-primary-950/30 mb-1">{t.status.sectionLabel}</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <span className={`w-2.5 h-2.5 rounded-full ${selectedConfig.dot}`} />
                                                <h2 className="text-xl text-primary-950 uppercase leading-none" style={{ fontFamily: 'Impact, sans-serif' }}>
                                                    {t.status.labels[selectedStatus] || selectedStatus}
                                                </h2>
                                            </div>
                                        </div>

                                        {isFailedOrder ? (
                                            <div className="p-6 flex flex-col items-center gap-3 text-center">
                                                <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
                                                    <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                                    </svg>
                                                </div>
                                                <p className="text-xs font-bold text-red-400">Status locked — payment failed orders cannot be updated.</p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="p-4 space-y-2">
                                                    {statusFlow.map((s) => {
                                                        const sc        = STATUS_CONFIG[s];
                                                        const icon      = statusIcons[s];
                                                        const isSelected = selectedStatus === s;
                                                        const isSaved   = s === order?.status && !isSelected;
                                                        return (
                                                            <button
                                                                key={s}
                                                                onClick={() => setSelectedStatus(s)}
                                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border-2 transition-all text-left
                                                                    ${isSelected
                                                                        ? 'bg-primary-950 border-primary-950'
                                                                        : 'bg-gray-50 border-transparent hover:border-gray-200'}`}
                                                            >
                                                                <div className={`w-10 h-10 rounded-2xl shrink-0 flex items-center justify-center p-2.5 transition-colors
                                                                    ${isSelected ? 'bg-secondary-500' : 'bg-white border border-gray-100'}`}
                                                                >
                                                                    {icon?.image ? (
                                                                        <MediaComponent
                                                                            media={icon.image}
                                                                            useSekeleton={false}
                                                                            className={`w-full h-full object-contain ${isSelected ? 'brightness-0 invert' : 'opacity-100'}`}
                                                                        />
                                                                    ) : (
                                                                        <span className={`w-2 h-2 rounded-full ${sc.dot}`} />
                                                                    )}
                                                                </div>
                                                                <span className={`text-sm font-black flex-1 ${isSelected ? 'text-white' : 'text-gray-500'}`}>
                                                                    {t.status.labels[s]}
                                                                </span>
                                                                {isSelected && (
                                                                    <svg className="w-4 h-4 text-secondary-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                )}
                                                                {isSaved && (
                                                                    <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                                                                        <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                                        </svg>
                                                                    </div>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                <div className="px-4 pb-5">
                                                    <ButtonFlowHover
                                                        onClick={handleUpdate}
                                                        disabled={!isDirty || saving}
                                                        loading={saving}
                                                        fullWidth
                                                        size="md"
                                                        className={!isDirty ? 'opacity-30 cursor-not-allowed pointer-events-none' : ''}
                                                    >
                                                        {t.status.updateBtn}
                                                    </ButtonFlowHover>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Print Label card */}
                                    <div className="bg-white rounded-[2rem] border border-gray-100 p-6 flex flex-col gap-4">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest">{t.print.sectionLabel}</p>
                                            {rolloLogoUrl && (
                                                <img src={rolloLogoUrl} alt="Rollo" className="h-5 w-auto object-contain" />
                                            )}
                                        </div>

                                        {order?.labelUrl ? (
                                            <ButtonFlowHover
                                                onClick={handlePrint}
                                                disabled={printing}
                                                loading={printing}
                                                fullWidth
                                                size="md"
                                            >
                                                {t.print.printBtn}
                                            </ButtonFlowHover>
                                        ) : order?.easyshipShipmentId ? (
                                            <>
                                                <p className="text-xs text-gray-400 font-medium text-center">{t.print.noLabel}</p>
                                                <ButtonChevronReveal
                                                    onClick={handleBuyLabel}
                                                    disabled={buyingLabel}
                                                    loading={buyingLabel}
                                                    fullWidth
                                                    size="md"
                                                >
                                                    {t.print.buyBtn}
                                                </ButtonChevronReveal>
                                            </>
                                        ) : (
                                            <p className="text-xs text-gray-300 font-medium text-center">{t.print.noShipment}</p>
                                        )}
                                    </div>
                                    {/* Invoice card */}
                                    <div className="bg-white rounded-[2rem] border border-gray-100 p-6 flex flex-col gap-4">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs font-bold text-primary-950/30 uppercase tracking-widest">Invoice</p>
                                            <svg className="w-4 h-4 text-primary-950/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                        </div>
                                        <ButtonFlowHover
                                            onClick={handleDownloadInvoice}
                                            disabled={downloadingInvoice}
                                            loading={downloadingInvoice}
                                            fullWidth
                                            size="md"
                                        >
                                            {downloadingInvoice ? 'Fetching...' : 'Download Invoice'}
                                        </ButtonFlowHover>
                                    </div>
                                </motion.div>
                            </div>
                        </motion.div>
                    )}
                </div>
            </Layout>
            <Footer />
        </div>
    );
}

export default AdminOrderDetail;
